import DateTimeField from '../components/DateTimeField';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { fmtRange } from '../utils/date';
import { useLanguage } from '../context/LanguageContext';
import Modal from '../components/Modal';
import { FormField, Input, Select, SubmitBtn } from '../components/FormField';
import './LeaveRequests.css';

const user = JSON.parse(localStorage.getItem('user') || '{}');

function StepIndicator({ status, t, isManagerOwn, managerIsDirector }) {
  /* Rəhbərin özü baş direktordursa, sonda yenidən onun imzasını gözləmək
     mənasızdır — zəncir HR-də bitir. Həmin halda "Direktor" mərhələsi
     ümumiyyətlə göstərilmir, yoxsa heç vaxt gəlməyəcək addım görünür. */
  const steps = managerIsDirector
    ? [
        { label: t('leave.stepSubmitted') },
        { label: t('leave.stepStage1') },
        { label: t('leave.stepHR') },
      ]
    : isManagerOwn
    ? [
        { label: t('leave.stepSubmitted') },
        { label: t('leave.stepHR') },
        { label: t('leave.stepDirector') },
      ]
    : [
        { label: t('leave.stepSubmitted') },
        { label: t('leave.stepStage1') },
        { label: t('leave.stepHR') },
        { label: t('leave.stepDirector') },
      ];

  const stateMap = managerIsDirector
    ? {
        submitted:         ['done','active','pending'],
        manager_approved:  ['done','done','active'],
        manager_rejected:  ['done','rejected','pending'],
        hr_approved:       ['done','done','done'],
        hr_rejected:       ['done','done','rejected'],
        // HR təsdiqi ilə birbaşa bura keçilir — zəncir tamamlanıb
        director_approved: ['done','done','done'],
        director_rejected: ['done','done','rejected'],
      }
    : isManagerOwn
    ? {
        submitted:         ['done','active','pending'],
        manager_approved:  ['done','active','pending'],
        hr_approved:       ['done','done','active'],
        hr_rejected:       ['done','rejected','pending'],
        director_approved: ['done','done','done'],
        director_rejected: ['done','done','rejected'],
      }
    : {
        submitted:         ['done','active','pending','pending'],
        manager_approved:  ['done','done','active','pending'],
        manager_rejected:  ['done','rejected','pending','pending'],
        hr_approved:       ['done','done','done','active'],
        hr_rejected:       ['done','done','rejected','pending'],
        director_approved: ['done','done','done','done'],
        director_rejected: ['done','done','done','rejected'],
      };
  const stateRow = stateMap[status] || steps.map(() => 'pending');

  return (
    <div className="lr-steps">
      {steps.map((s, i) => {
        const cls = stateRow[i];
        const done = cls === 'done';
        const active = cls === 'active';
        const rejected = cls === 'rejected';
        return (
          <React.Fragment key={i}>
            <div className={`lr-step ${cls}`}>
              <div className="lr-step-icon">
                {done ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                ) : rejected ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                ) : active ? (
                  <span className="lr-pulse-dot" />
                ) : (
                  <span style={{ fontSize: '0.78rem', fontWeight: 700 }}>{i + 1}</span>
                )}
              </div>
              <div className="lr-step-label">
                {s.label}
                {active && <span className="lr-waiting-tag"> · {t('leave.pendingDot')}</span>}
              </div>
            </div>
            {i < steps.length - 1 && <div className={`lr-step-line ${done ? 'done' : ''}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function LeaveRequests() {
  const { t, lang } = useLanguage();
  const ABSENCE_TYPES_T = [
    { value: 'medical',   label: t('leave.medical'),   desc: t('leave.medicalDesc') },
    { value: 'family',    label: t('leave.family'),    desc: t('leave.familyDesc') },
    { value: 'education', label: t('leave.education'), desc: t('leave.educationDesc') },
    { value: 'other',     label: t('leave.other'),     desc: t('leave.otherDesc') },
  ];
  const FAMILY_REASONS_T = [
    { value: 'own_wedding',     label: t('leave.famReasonOwnWedding') },
    { value: 'family_wedding',  label: t('leave.famReasonFamilyWedding') },
    { value: 'family_death',    label: t('leave.famReasonFamilyDeath') },
    { value: 'relative_death',  label: t('leave.famReasonRelativeDeath') },
  ];
  const DISC_OPTIONS_T = [
    { value: '',          label: t('leave.discNone') },
    { value: 'warning',   label: t('leave.discWarning') },
    { value: 'sanction',  label: t('leave.discSanction') },
    { value: 'terminate', label: t('leave.discTerminate') },
  ];
  const STATUS_MAP_T = {
    submitted:         { label: t('leave.statusSubmitted'),    color: '#d97706', bg: '#fef3c7' },
    manager_approved:  { label: t('leave.statusMgrApproved'),  color: '#7c3aed', bg: '#ede9fe' },
    manager_rejected:  { label: t('leave.statusMgrRejected'),  color: '#dc2626', bg: '#fee2e2' },
    hr_approved:       { label: t('leave.statusHrApproved'),   color: '#0369a1', bg: '#dbeafe' },
    hr_rejected:       { label: t('leave.statusHrRejected'),   color: '#dc2626', bg: '#fee2e2' },
    director_approved: { label: t('leave.statusDirApproved'),  color: '#059669', bg: '#d1fae5' },
    director_rejected: { label: t('leave.statusDirRejected'),  color: '#dc2626', bg: '#fee2e2' },
  };
  const locale = lang === 'en' ? 'en-US' : 'az-AZ';
  const [leaves, setLeaves]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [selected, setSelected]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const [me, setMe] = useState(null);
  useEffect(() => { API.get('/auth/me').then(r => setMe(r.data)).catch(() => {}); }, []);

  const [absType, setAbsType]     = useState('');
  const [famReason, setFamReason] = useState('');
  const [eduDoc, setEduDoc]       = useState('');
  /* "Bəli, sənəd var" seçiləndə arayışın özü də yüklənir — rəhbər, HR və
     direktor ərizənin içində onu açıb baxa bilsin */
  const [eduFiles, setEduFiles]   = useState([]);
  const [attachments, setAttachments] = useState([]);
  /* Toplu çap: siyahıdan bir neçə ərizə seçilib tək PDF kimi endirilir */
  const [secilenler, setSecilenler] = useState([]);
  const [pdfBusy, setPdfBusy]       = useState(false);
  const [explanation, setExpl]    = useState('');
  const [startDate, setStart]     = useState('');
  const [endDate, setEnd]         = useState('');

  const [approveNote, setApproveNote]   = useState('');
  /* Ödənişli/ödənişsiz QABAQCADAN seçilmir — HR şüurlu qərar versin.
     Əvvəl 'paid' hazır gəlirdi və diqqət edilməsə ödənişli kimi gedirdi. */
  const [hrDecision, setHrDecision]     = useState('');
  const [discEmployee, setDiscEmployee] = useState('');
  const [discManager, setDiscManager]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const r = await API.get('/leave', { params });
      setLeaves(r.data);
    } catch (e) {} finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onNew     = (e) => setLeaves(prev => prev.find(l => l.id === e.detail.id) ? prev : [e.detail, ...prev]);
    const onUpdated = (e) => {
      setLeaves(prev => prev.map(l => l.id === e.detail.id ? e.detail : l));
      setSelected(prev => prev?.id === e.detail.id ? e.detail : prev);
    };
    window.addEventListener('ems:leave:new', onNew);
    window.addEventListener('ems:leave:updated', onUpdated);
    return () => {
      window.removeEventListener('ems:leave:new', onNew);
      window.removeEventListener('ems:leave:updated', onUpdated);
    };
  }, []);

  const openDetail = async (leave) => {
    setHrDecision('');          // əvvəlki ərizənin seçimi daşınmasın
    setAttachments([]);
    try {
      const [r, att] = await Promise.all([
        API.get('/leave/' + leave.id),
        API.get('/leave/' + leave.id + '/attachments').catch(() => ({ data: [] })),
      ]);
      setSelected(r.data);
      setAttachments(att.data || []);
      setModal('detail');
    } catch (e) { setSelected(leave); setModal('detail'); }
  };

  /* Kağız forma: serverdə PDF yaranır, fayl kimi endirilir.
     Bir neçə ərizə seçiləndə hamısı tək faylda, hər biri ayrı səhifədə. */
  const pdfEndir = async (ids, { bax = false } = {}) => {
    if (!ids.length || pdfBusy) return;
    setPdfBusy(true);
    try {
      const yol = ids.length === 1 ? '/leave/' + ids[0] + '/pdf' : '/leave/pdf?ids=' + ids.join(',');
      const r = await API.get(yol, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      if (bax) {
        // İlkin baxış — fayl endirilmir, yeni pəncərədə açılır
        window.open(url, '_blank', 'noopener');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = ids.length === 1
          ? 'isburaxma-ISB-' + ids[0] + '.pdf'
          : 'isburaxma-erizeleri-' + ids.length + '.pdf';
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      // responseType=blob olduğu üçün server xətası da blob gəlir — mətnə çeviririk
      let msg = t('leave.genericError');
      try { msg = JSON.parse(await err.response.data.text()).message || msg; } catch {}
      alert(msg);
    } finally { setPdfBusy(false); }
  };

  const secimDeyis = (id) => setSecilenler(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const fmtSize = (b) => !b ? '' :
    b < 1024 ? b + ' B' :
    b < 1048576 ? Math.round(b / 1024) + ' KB' :
    (b / 1048576).toFixed(1) + ' MB';

  /* Fayl auth tələb edir — blob kimi yüklənib yeni pəncərədə açılır */
  const openAttachment = async (a) => {
    try {
      const r = await API.get('/leave/' + selected.id + '/attachments/' + a.id, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert(err.response?.data?.message || t('leave.genericError'));
    }
  };

  /* E-poçtdakı "Ərizəyə bax" linki /leave?id=NN şəklindədir —
     səhifə açılan kimi həmin ərizəni açırıq. */
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('id');
    if (id) openDetail({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    /* Ümumi "sahələri doldurun" mesajı hansı sahənin problemli olduğunu
       demirdi. Tarix sahəsi dolu görünüb dəyər qaytarmaya bilər, ona görə
       tarix üçün ayrıca, formatı izah edən mesaj verilir. */
    if (!absType) return alert(t('leave.requiredFieldsAlert'));
    if (!startDate || !endDate) return alert(t('leave.badDateAlert'));
    // "Sənəd var" deyilibsə, sənədin özü də olmalıdır — yoxsa təsdiqləyən boşluq görür
    if (absType === 'education' && eduDoc === 'yes' && !eduFiles.length)
      return alert(t('leave.docRequiredAlert'));
    setSaving(true);
    try {
      const r = await API.post('/leave', {
        absence_type: absType,
        family_reason: absType === 'family' ? famReason : undefined,
        education_has_doc: absType === 'education' ? (eduDoc === 'yes' ? 1 : 0) : undefined,
        explanation,
        start_date: startDate,
        end_date: endDate,
      });
      if (eduFiles.length && r.data?.id) {
        const fd = new FormData();
        eduFiles.forEach(f => fd.append('files', f));
        await API.post('/leave/' + r.data.id + '/attachments', fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setModal(null);
      setAbsType(''); setFamReason(''); setEduDoc(''); setExpl(''); setStart(''); setEnd('');
      setEduFiles([]);
      load();
    } catch (err) {
      alert(err.response?.data?.error || t('leave.genericError'));
    } finally { setSaving(false); }
  };

  const handleManagerAction = async (action) => {
    setSaving(true);
    try {
      await API.put('/leave/' + selected.id + '/manager', { action, note: approveNote });
      setModal(null); setApproveNote('');
      load();
    } catch (err) { alert(err.response?.data?.error || t('leave.genericError')); } finally { setSaving(false); }
  };

  const handleHrAction = async (action) => {
    setSaving(true);
    try {
      await API.put('/leave/' + selected.id + '/hr', {
        action, hr_decision: hrDecision, hr_note: approveNote,
        disciplinary_employee: discEmployee || null,
        disciplinary_manager: discManager || null,
      });
      setModal(null); setApproveNote(''); setDiscEmployee(''); setDiscManager('');
      load();
    } catch (err) { alert(err.response?.data?.error || t('leave.genericError')); } finally { setSaving(false); }
  };

  const handleDirectorAction = async (action) => {
    setSaving(true);
    try {
      await API.put('/leave/' + selected.id + '/director', { action, note: approveNote });
      setModal(null); setApproveNote('');
      load();
    } catch (err) { alert(err.response?.data?.error || t('leave.genericError')); } finally { setSaving(false); }
  };

  const isOwner = selected?.employee_id === user.id;
  const myDept = me?.department_name || '';
  const myPos  = me?.position || user.position || '';
  const isHRPerson = /HR|İnsan|Insan/i.test(myDept) || /HR|İnsan|Insan/i.test(myPos);
  /* Direktorluq vəzifə mətnindən oxunurdu və "Məktəb direktoru" da bu şərtə
     düşürdü — məktəb direktoru baş direktorun düymələrini görürdü.
     İndi server users.is_director sahəsini verir, mətnlə iş yoxdur. */
  const isDirectorPerson = user.role === 'admin' ||
    Number(me?.is_director ?? user.is_director) === 1;
  /* Təsdiqi ROL yox, BİRBAŞA RƏHBƏRLİK həll edir.
     Ərizəyə rəhbər təyin olunubsa, təsdiq düyməsi məhz ona görünür —
     vəzifəsindən asılı olmayaraq (məs. "Orta təhsil pilləsi üzrə lider"
     staff rolundadır, amma müəllimin birbaşa rəhbəridir).
     Rəhbər təyin olunmayıbsa, köhnə qayda qalır: şöbə rəhbəri.
     Server də eyni şərti yoxlayır. */
  const isAssignedManager = selected?.manager_id
    && String(selected.manager_id) === String(user.id);
  const canManagerAct = !isOwner && selected?.status === 'submitted' && (
    isAssignedManager
    || user.role === 'admin'
    || (!selected?.manager_id && user.role === 'department_head')
  );
  const canHrAct       = !isOwner && selected?.status === 'manager_approved' && (user.role === 'admin' || isHRPerson);
  const canDirectorAct = !isOwner && selected?.status === 'hr_approved' && isDirectorPerson;
  const noActions = !canManagerAct && !canHrAct && !canDirectorAct;

  /* Sorğular səhifəsindən kampus seçilib gəlinibsə (?branch=2), yalnız
     həmin kampusun ərizələri göstərilir */
  const brParam = new URLSearchParams(window.location.search).get('branch');
  const filtered = leaves.filter(l =>
    (!filterStatus || l.status === filterStatus) &&
    (!brParam || String(l.branch_id) === String(brParam)));
  const pending  = leaves.filter(l => ['submitted','manager_approved','hr_approved'].includes(l.status)).length;

  /* "Sizin təsdiqinizi gözləyir" yalnız NÖVBƏ məndə olanda doğrudur.
     Əvvəl sadəcə bitməmiş ərizələr sayılırdı — rəhbər öz hissəsini
     çoxdan təsdiqləyib HR-ə ötürsə də, say onun üzərində qalırdı.
     Admin burada sayılmır: o, hər mərhələyə müdaxilə edə bilsə də,
     ərizə əslində onu gözləmir. */
  const menimNovbem = (l) => {
    if (!l || String(l.employee_id) === String(user.id)) return false;
    if (l.status === 'submitted')
      return (l.manager_id && String(l.manager_id) === String(user.id))
          || (!l.manager_id && user.role === 'department_head');
    if (l.status === 'manager_approved') return isHRPerson;
    if (l.status === 'hr_approved') return isDirectorPerson && user.role !== 'admin';
    return false;
  };
  const meniGozleyen = leaves.filter(menimNovbem).length;

  /* Hamısını seç yalnız süzgəcdən keçən ərizələrə işləyir —
     görünməyən ərizənin gizlicə seçilməsi çaşdırıcı olardı. */
  const hamisiSecilib = filtered.length > 0 && filtered.every(l => secilenler.includes(l.id));
  const qismenSecilib = !hamisiSecilib && filtered.some(l => secilenler.includes(l.id));
  const hamisiniSec = () => setSecilenler(hamisiSecilib ? [] : filtered.map(l => l.id));

  const nav = useNavigate();
  return (
    <div className="lr-page">
      {/* Başlıq, filtr və siyahı bir panelin bölmələridir */}
      <div className="lr-panel">
      <div className="lr-header">
        <button className="lr-back" onClick={() => nav('/requests')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div className="lr-header-txt">
          <h2>{t('leave.title')}</h2>
          <span className="lr-sub">{leaves.length} {t('leave.requestSuffix')}{pending > 0 ? ` ${t('leave.countDot')} ${pending} ${t('leave.pendingSuffix')}` : ''}</span>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('new')}>{t('leave.btnNew')}</button>
      </div>

      <div className="lr-filters">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">{t('leave.allStatuses')}</option>
          {Object.entries(STATUS_MAP_T).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
        </select>
        {meniGozleyen > 0 ? (
          <span className="lr-pending-chip">
            <span className="lr-pulse-dot" style={{ background: '#d97706' }} />
            {meniGozleyen} {t('leave.pendingApproval')}
          </span>
        ) : pending > 0 && (
          <span className="lr-pending-chip neytral">
            <span className="lr-pulse-dot" style={{ background: '#94a3b8' }} />
            {pending} {t('leave.inProcess')}
          </span>
        )}
      </div>

      {loading ? (
        <div className="lr-empty">{t('leave.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="lr-empty">{t('leave.noRequests')}</div>
      ) : (
        <div className="lr-list">
          {/* Çap zolağı həmişə görünür — soldakı xana siyahının başındakı
              kartların xanaları ilə eyni sütunda durur */}
          <div className={'lr-bulk-bar' + (secilenler.length ? '' : ' passiv')}>
            <label className="lr-select-all">
              <input
                type="checkbox"
                className="lr-card-check"
                checked={hamisiSecilib}
                ref={el => { if (el) el.indeterminate = qismenSecilib; }}
                onChange={hamisiniSec}
              />
              <span>{t('leave.selectAll')}</span>
            </label>
            {secilenler.length > 0 && (
              <>
                <span className="lr-bulk-count">{secilenler.length} {t('leave.selectedCount')}</span>
                <button type="button" className="lr-pdf-btn" disabled={pdfBusy}
                        onClick={() => pdfEndir(secilenler)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  {pdfBusy ? t('leave.pdfPreparing') : t('leave.btnPdfSelected')}
                </button>
                <button type="button" className="lr-bulk-clear" onClick={() => setSecilenler([])}>
                  {t('leave.clearSelection')}
                </button>
              </>
            )}
          </div>
          {filtered.map(l => {
            const at = ABSENCE_TYPES_T.find(a => a.value === l.absence_type);
            const stT = STATUS_MAP_T[l.status] || {};
            const days = l.work_days || (l.end_date && l.start_date
              ? Math.round((new Date(l.end_date) - new Date(l.start_date)) / 86400000) + 1
              : '?');
            return (
              <div key={l.id} className={'lr-card' + (menimNovbem(l) ? ' menim' : '')} onClick={() => openDetail(l)}>
                <div className="lr-card-left">
                  {/* Çap üçün seçim — karta klik ərizəni açdığı üçün hadisəni dayandırırıq */}
                  <input
                    type="checkbox"
                    className="lr-card-check"
                    checked={secilenler.includes(l.id)}
                    onClick={e => e.stopPropagation()}
                    onChange={() => secimDeyis(l.id)}
                    title={t('leave.selectForPrint')}
                  />
                  <div>
                    <div className="lr-card-name">{l.employee_name}</div>
                    <div className="lr-card-sub">{(l.department_name && lang === 'en' ? t('departments.' + l.department_name, l.department_name) : l.department_name) || '—'} · {at?.label}</div>
                  </div>
                </div>
                <div className="lr-card-mid">
                  {/* Saat da göstərilir — yarımgünlük icazələrdə
                      yalnız tarix görünəndə vaxt itirdi */}
                  <div className="lr-dates">{fmtRange(l.start_date, l.end_date, lang)}</div>
                  <div className="lr-days">{days} {t('leave.days')}</div>
                </div>
                <div className="lr-card-right">
                  <span style={{ background: stT.bg, color: stT.color, padding: '3px 10px', borderRadius: 99, fontSize: '0.73rem', fontWeight: 700 }}>{stT.label}</span>
                  {['submitted','manager_approved','hr_approved'].includes(l.status) && (
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span className="lr-pulse-dot" style={{ width: 6, height: 6, background: '#9ca3af' }} />
                      {l.status === 'submitted' ? t('leave.waitMgr') : l.status === 'manager_approved' ? t('leave.waitHR') : t('leave.waitDir')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>

      {modal === 'new' && (
        <Modal title={t('leave.newRequest')} onClose={() => setModal(null)}>
          <form onSubmit={handleSubmit}>
            <FormField label={t('leave.type')}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginTop: 4 }}>
                {ABSENCE_TYPES_T.map(it => (
                  <label key={it.value} style={{
                    border: '1.5px solid ' + (absType === it.value ? '#007c82' : '#e5e7eb'),
                    background: absType === it.value ? '#e0f2f4' : '#fff',
                    borderRadius: 10, padding: '11px 12px', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', gap: 3, transition: 'all 0.12s'
                  }}>
                    <input type="radio" style={{ display: 'none' }} checked={absType === it.value}
                      onChange={() => { setAbsType(it.value); setFamReason(''); setEduDoc(''); }} />
                    <span style={{ fontWeight: 700, fontSize: '0.88rem', color: absType === it.value ? '#007c82' : '#374151' }}>{it.label}</span>
                    <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{it.desc}</span>
                  </label>
                ))}
              </div>
            </FormField>

            {absType === 'family' && (
              <FormField label={t('leave.familyReasonLabel')}>
                <Select value={famReason} onChange={e => setFamReason(e.target.value)} required>
                  <option value="">{t('leave.select')}</option>
                  {FAMILY_REASONS_T.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </Select>
              </FormField>
            )}

            {absType === 'education' && (
              <FormField label={t('leave.eduDocLabel')}>
                <Select value={eduDoc} onChange={e => { setEduDoc(e.target.value); if (e.target.value !== 'yes') setEduFiles([]); }}>
                  <option value="">{t('leave.select')}</option>
                  <option value="yes">{t('leave.eduDocYes')}</option>
                  <option value="no">{t('leave.eduDocNo')}</option>
                </Select>
              </FormField>
            )}

            {absType === 'education' && eduDoc === 'yes' && (
              <FormField label={t('leave.docUploadLabel')}>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.heic,.webp,.doc,.docx"
                  onChange={e => setEduFiles(Array.from(e.target.files || []).slice(0, 3))}
                  style={{ width: '100%', padding: '8px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 4 }}>
                  {t('leave.docUploadHint')}
                </div>
                {eduFiles.length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: '0.78rem', color: '#374151' }}>
                    {eduFiles.map((f, i) => (
                      <li key={i}>{f.name} <span style={{ color: '#9ca3af' }}>({fmtSize(f.size)})</span></li>
                    ))}
                  </ul>
                )}
              </FormField>
            )}

            <div className="form-row">
              <FormField label={t('leave.startDate')}>
                <DateTimeField value={startDate} onChange={setStart} required />
              </FormField>
              <FormField label={t('leave.endDate')}>
                <DateTimeField value={endDate} onChange={setEnd} required />
              </FormField>
            </div>

            {['medical','education','other'].includes(absType) && (
              <FormField label={absType === 'medical' ? t('leave.explanationNote') : t('leave.explanation')}>
                <textarea
                  value={explanation} onChange={e => setExpl(e.target.value)}
                  required
                  rows={3}
                  style={{ width: '100%', padding: '8px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.88rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                  placeholder={t('leave.explanationPlaceholder')}
                />
              </FormField>
            )}

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>{t('leave.btnCancel')}</button>
              <SubmitBtn loading={saving}>{t('leave.btnSubmit')}</SubmitBtn>
            </div>
          </form>
        </Modal>
      )}

      {modal === 'detail' && selected && (
        <Modal title={`${t('leave.requestNum')} #${selected.id} — ${selected.employee_name}`} onClose={() => setModal(null)}>
          <StepIndicator
            status={selected.status}
            t={t}
            managerIsDirector={!!Number(selected.manager_is_director)}
            isManagerOwn={!selected.manager_id && (selected.status === 'manager_approved' || selected.status === 'hr_approved' || selected.status === 'hr_rejected' || selected.status === 'director_approved' || selected.status === 'director_rejected')}
          />

          {/* Təsdiq bitməyibsə yalnız ilkin baxış — sənəd "LAYİHƏ" nişanı ilə
              brauzerdə açılır. Endirilən rəsmi PDF yalnız hamı təsdiqləyəndən sonra. */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            {selected.status === 'director_approved' ? (
              <button type="button" className="lr-pdf-btn" onClick={() => pdfEndir([selected.id])}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {t('leave.btnPdf')}
              </button>
            ) : (
              <button type="button" className="lr-pdf-btn ghost"
                      onClick={() => pdfEndir([selected.id], { bax: true })}
                      title={t('leave.previewHint')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
                {t('leave.btnPreview')}
              </button>
            )}
          </div>

          <div className="lr-detail-grid">
            <div className="lr-detail-section">
              <div className="lr-detail-label">{t('leave.detailEmployee')}</div>
              <div className="lr-detail-val">{selected.employee_name}</div>
            </div>
            <div className="lr-detail-section">
              <div className="lr-detail-label">{t('leave.detailDeptPos')}</div>
              <div className="lr-detail-val">{selected.department_name || '—'} / {selected.position || '—'}</div>
            </div>
            <div className="lr-detail-section">
              <div className="lr-detail-label">{t('leave.detailAbsType')}</div>
              <div className="lr-detail-val">
                {ABSENCE_TYPES_T.find(a => a.value === selected.absence_type)?.label}
                {selected.family_reason && ` — ${FAMILY_REASONS_T.find(r => r.value === selected.family_reason)?.label}`}
              </div>
            </div>
            <div className="lr-detail-section">
              <div className="lr-detail-label">{t('leave.detailDate')}</div>
              {/* SAAT da göstərilir: yarımgünlük icazələrdə (15:30–17:00)
                  yalnız tarix görünəndə vaxt itirdi. Eyni gündürsə tarix
                  bir dəfə yazılır, saat aralığı yanında verilir. */}
              <div className="lr-detail-val">{fmtRange(selected.start_date, selected.end_date, lang)}</div>
            </div>
            {selected.explanation && (
              <div className="lr-detail-section" style={{ gridColumn: '1/-1' }}>
                <div className="lr-detail-label">{t('leave.detailExplanation')}</div>
                <div className="lr-detail-val" style={{ background: '#f9fafb', padding: '8px 12px', borderRadius: 7, border: '1px solid #e5e7eb' }}>{selected.explanation}</div>
              </div>
            )}

            {/* Əlavə olunmuş arayış — rəhbər, HR və direktor buradan açır */}
            {attachments.length > 0 && (
              <div className="lr-detail-section" style={{ gridColumn: '1/-1' }}>
                <div className="lr-detail-label">{t('leave.detailDocuments')}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                  {attachments.map(a => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => openAttachment(a)}
                      title={t('leave.docOpenHint')}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                               background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7,
                               padding: '8px 12px', cursor: 'pointer', fontSize: '0.84rem', color: '#007c82', fontWeight: 600 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      <span style={{ flex: 1 }}>{a.original_name}</span>
                      <span style={{ color: '#9ca3af', fontWeight: 400 }}>{fmtSize(a.size_bytes)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* "Sənəd var" deyilib, amma fayl yoxdursa — təsdiqləyən bunu bilsin */}
            {selected.absence_type === 'education' && selected.education_has_doc === 1 && attachments.length === 0 && (
              <div className="lr-detail-section" style={{ gridColumn: '1/-1' }}>
                <div className="lr-detail-val" style={{ background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e',
                                                        padding: '8px 12px', borderRadius: 7, fontSize: '0.82rem' }}>
                  {t('leave.docMissingWarn')}
                </div>
              </div>
            )}
          </div>

          <div className="lr-history">
            {selected.manager_id && (
              <div className={`lr-history-item ${selected.status === 'manager_rejected' ? 'rejected' : 'approved'}`}>
                <span className="lr-history-icon">
                  {selected.status === 'manager_rejected' ? (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  )}
                </span>
                <div>
                  <b>{t('leave.histStage1')}</b>: {selected.manager_name}
                  {selected.manager_note && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>"{selected.manager_note}"</div>}
                </div>
                <span className="lr-history-date">{selected.manager_approved_at ? new Date(selected.manager_approved_at).toLocaleDateString(locale) : ''}</span>
              </div>
            )}
            {selected.hr_user_id && (
              <div className={`lr-history-item ${selected.status === 'hr_rejected' ? 'rejected' : 'approved'}`}>
                <span className="lr-history-icon">
                  {selected.status === 'hr_rejected' ? (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  )}
                </span>
                <div>
                  <b>{t('leave.histHR')}</b>: {selected.hr_name}
                  {selected.hr_decision && <span style={{ marginLeft: 8, fontSize: '0.8rem', fontWeight: 700, color: '#059669' }}>{selected.hr_decision === 'paid' ? t('leave.hrPaid') : t('leave.hrUnpaid')}</span>}
                  {selected.hr_note && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>"{selected.hr_note}"</div>}
                </div>
                <span className="lr-history-date">{selected.hr_approved_at ? new Date(selected.hr_approved_at).toLocaleDateString(locale) : ''}</span>
              </div>
            )}
            {selected.director_id && (
              <div className={`lr-history-item ${selected.status === 'director_rejected' ? 'rejected' : 'approved'}`}>
                <span className="lr-history-icon">
                  {selected.status === 'director_rejected' ? (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  )}
                </span>
                <div>
                  <b>{t('leave.histDirector')}</b>: {selected.director_name}
                  {selected.director_note && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>"{selected.director_note}"</div>}
                </div>
                <span className="lr-history-date">{selected.director_approved_at ? new Date(selected.director_approved_at).toLocaleDateString(locale) : ''}</span>
              </div>
            )}
          </div>

          {canManagerAct && (
            <div className="lr-action-panel">
              <div className="lr-action-title">{t('leave.mgrTitle')}</div>
              <FormField label={t('leave.mgrNoteLabel')}>
                <textarea value={approveNote} onChange={e => setApproveNote(e.target.value)} rows={2}
                  style={{ width: '100%', padding: '8px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.87rem', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }}
                  placeholder={t('leave.mgrNotePlaceholder')} />
              </FormField>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-danger" onClick={() => handleManagerAction('reject')} disabled={saving}>{t('leave.btnReject')}</button>
                <button className="btn btn-primary" onClick={() => handleManagerAction('approve')} disabled={saving}>{t('leave.btnApprove')}</button>
              </div>
            </div>
          )}

          {canHrAct && (
            <div className="hr-panel-v2">
              <div className="hr-panel-header">
                <div>
                  <div className="hr-panel-title">{t('leave.hrPanelTitle')}</div>
                  <div className="hr-panel-sub">{t('leave.hrPanelSub')}</div>
                </div>
              </div>

              <div className="hr-block">
                <div className="hr-block-label">{t('leave.hrLeaveType')}<span className="hr-required">*</span></div>
                <div className="hr-decision-grid">
                  <div className={`hr-decision-card ${hrDecision === 'paid' ? 'sel' : ''}`} style={{ ['--c']: '#059669' }} onClick={() => setHrDecision('paid')}>
                    <div className="hr-decision-name">{t('leave.hrPaid')}</div>
                    <div className="hr-decision-desc">{t('leave.hrPaidDesc')}</div>
                    {hrDecision === 'paid' && <div className="hr-decision-check"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></div>}
                  </div>
                  <div className={`hr-decision-card ${hrDecision === 'unpaid' ? 'sel' : ''}`} style={{ ['--c']: '#6b7280' }} onClick={() => setHrDecision('unpaid')}>
                    <div className="hr-decision-name">{t('leave.hrUnpaid')}</div>
                    <div className="hr-decision-desc">{t('leave.hrUnpaidDesc')}</div>
                    {hrDecision === 'unpaid' && <div className="hr-decision-check"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></div>}
                  </div>
                </div>
                {/* Seçim edilməyincə göndərmə düyməsi bağlıdır — səbəbi yazılır */}
                {!hrDecision && (
                  <div className="hr-need-pick">{t('leave.hrPickFirst')}</div>
                )}
              </div>

              <div className="hr-block">
                <div className="hr-block-label">{t('leave.hrDiscMeasures')}</div>
                <div className="form-row">
                  <FormField label={t('leave.hrDiscEmployee')}>
                    <Select value={discEmployee} onChange={e => setDiscEmployee(e.target.value)}>
                      {DISC_OPTIONS_T.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                  </FormField>
                  <FormField label={t('leave.hrDiscManager')}>
                    <Select value={discManager} onChange={e => setDiscManager(e.target.value)}>
                      {DISC_OPTIONS_T.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Select>
                  </FormField>
                </div>
              </div>

              <div className="hr-block">
                <div className="hr-block-label">{t('leave.hrNote')}</div>
                <textarea
                  className="hr-note-input"
                  value={approveNote}
                  onChange={e => setApproveNote(e.target.value)}
                  rows={3}
                  placeholder={t('leave.hrNotePlaceholder')}
                />
              </div>

              {hrDecision && (
                <div className="hr-preview">
                  <div className="hr-preview-title">{t('leave.hrPreviewTitle')}</div>
                  <div className="hr-preview-row">
                    <span>{t('leave.hrPreviewType')}</span>
                    <strong>{hrDecision === 'paid' ? t('leave.hrPaid') : t('leave.hrUnpaid')}</strong>
                  </div>
                  {discEmployee && (
                    <div className="hr-preview-row">
                      <span>{t('leave.hrPreviewEmp')}</span>
                      <strong>{DISC_OPTIONS_T.find(o => o.value === discEmployee)?.label}</strong>
                    </div>
                  )}
                  {discManager && (
                    <div className="hr-preview-row">
                      <span>{t('leave.hrPreviewMgr')}</span>
                      <strong>{DISC_OPTIONS_T.find(o => o.value === discManager)?.label}</strong>
                    </div>
                  )}
                  {approveNote && <div className="hr-preview-note">"{approveNote}"</div>}
                </div>
              )}

              <div className="hr-action-row">
                <button className="btn btn-danger" onClick={() => handleHrAction('reject')} disabled={saving}>{t('leave.btnReject')}</button>
                <button className="btn btn-primary hr-send-btn" onClick={() => handleHrAction('approve')} disabled={saving || !hrDecision}>{t('leave.btnSendDirector')}</button>
              </div>
            </div>
          )}

          {canDirectorAct && (
            <div className="hr-panel-v2">
              <div className="hr-panel-header">
                <div>
                  <div className="hr-panel-title">{t('leave.dirTitle')}</div>
                  <div className="hr-panel-sub">{t('leave.dirHrLabel')} <strong style={{ color: '#059669', marginLeft: 6 }}>{selected.hr_decision === 'paid' ? t('leave.dirPaid') : t('leave.dirUnpaid')}</strong></div>
                </div>
              </div>

              <div className="hr-block">
                <div className="hr-block-label">{t('leave.mgrNoteLabel')}</div>
                <textarea
                  className="hr-note-input"
                  value={approveNote}
                  onChange={e => setApproveNote(e.target.value)}
                  rows={3}
                  placeholder={t('leave.mgrNotePlaceholder')}
                />
              </div>

              <div className="hr-action-row">
                <button className="dir-reject-btn" onClick={() => handleDirectorAction('reject')} disabled={saving}>{t('leave.btnReject')}</button>
                <button className="dir-approve-btn" onClick={() => handleDirectorAction('approve')} disabled={saving}>{t('leave.btnApprove')}</button>
              </div>
            </div>
          )}

          {/* Print form — visible only to HR/admin AFTER director has approved */}
          {selected.status === 'director_approved' && (user.role === 'admin' || isHRPerson) && (
            <div style={{
              marginTop: 14, padding: '14px 16px',
              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
              border: '1px solid #6ee7b7', borderRadius: 12,
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: '#059669', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: '#065f46', fontSize: '0.92rem' }}>Çap forması hazırdır</div>
                <div style={{ fontSize: '0.78rem', color: '#047857' }}>Direktor təsdiqindən sonra rəsmi sənəd yaradıldı — QR kodla doğrulanır</div>
              </div>
              <button
                className="btn btn-primary"
                style={{ background: 'linear-gradient(135deg, #059669, #10b981)', boxShadow: '0 4px 12px rgba(5,150,105,0.32)', whiteSpace: 'nowrap' }}
                onClick={() => {
                  const token = localStorage.getItem('token') || '';
                  window.open(`/api/leave/${selected.id}/print?token=${encodeURIComponent(token)}`, '_blank');
                }}
              >
                Çap et
              </button>
            </div>
          )}

          {noActions && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>{t('leave.btnCancel')}</button>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
